/**
 * Display formatting. Kept out of components so a date renders identically
 * everywhere in the app.
 */

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  // Always label the timezone. Timestamps are stored in UTC but render in the
  // viewer's local zone, so an unlabelled time reads as a 1-hour (or more) gap
  // against backend logs and block explorers (which are UTC). The label
  // (e.g. "GMT+1") removes the ambiguity.
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/** Coarse relative time — enough for "last used", not a full i18n solution. */
export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return 'never';

  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;

  return formatDate(value);
}

export function formatNumber(value: number): string {
  return value.toLocaleString();
}

export function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

export function formatDuration(ms: number): string {
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;
}

/** Status-code colour bucket, used by the request log and delivery tables. */
export function statusTone(status: number): 'success' | 'warning' | 'danger' {
  if (status < 400) return 'success';
  if (status < 500) return 'warning';
  return 'danger';
}
